/**
 * Participant context: who was in the room, described at the level a facilitator needs for an
 * after-action report — and no finer.
 *
 * Privacy rules, enforced by the helpers in this file rather than by convention:
 *  - IP addresses are never stored or rendered. Only the coarse geography Cloudflare derives from
 *    them (city / region / country / continent / timezone) and the network operator name.
 *  - Email addresses are never stored. The edge masks an address on the way in and keeps only the
 *    mask plus the domain, so `taylor.brooks@example.com` becomes `t•••s@example.com` + `example.com`.
 *  - User agents are never stored. They are reduced at the edge to a device and a browser family.
 */

/** Everything the report knows about one participant beyond their name and score. */
export interface ParticipantContext {
  /** City name from the edge's geolocation (never a postcode or coordinates). */
  city: string | null;
  /** Region / state / province name. */
  region: string | null;
  /** ISO-3166-2 subdivision code without the country prefix, e.g. "TX". */
  regionCode: string | null;
  /** ISO-3166-1 alpha-2 country code, uppercase. */
  country: string | null;
  /** ISO continent code: AF, AN, AS, EU, NA, OC, SA. */
  continent: string | null;
  /** IANA time zone, e.g. "America/Chicago". */
  timezone: string | null;
  /** Network operator / ISP name (Cloudflare's `asOrganization`). */
  isp: string | null;
  /** Autonomous system number that announces the participant's network. */
  asn: number | null;
  /** Coarse device label, e.g. "iPhone" or "Windows PC". */
  device: string | null;
  /** Browser family, e.g. "Safari". */
  browser: string | null;
  /** Masked email address, e.g. "t•••s@example.com". Never the real address. */
  emailMask: string | null;
  /** Email domain, e.g. "example.com". */
  emailDomain: string | null;
  /** Where the address came from: the participant typed it, or the facilitator reserved the seat. */
  emailSource: "self" | "seat" | null;
  /** When this context was captured (unix ms). */
  capturedAt: number | null;
}

export const EMPTY_CONTEXT: ParticipantContext = {
  city: null,
  region: null,
  regionCode: null,
  country: null,
  continent: null,
  timezone: null,
  isp: null,
  asn: null,
  device: null,
  browser: null,
  emailMask: null,
  emailDomain: null,
  emailSource: null,
  capturedAt: null,
};

/** Character used to hide the middle of an email local part. */
export const MASK_CHAR = "•";

/** A plain, permissive address check — the point is masking, not validating deliverability. */
const EMAIL_RE = /^[^\s@,;:<>()[\]\\"]+@[^\s@.,;:<>()[\]\\"]+(\.[^\s@.,;:<>()[\]\\"]+)+$/;

export function isEmailLike(input: string): boolean {
  const s = input.trim();
  return s.length <= 254 && EMAIL_RE.test(s);
}

/**
 * Mask an address for the facilitator: keep the first and last character of the local part, hide the
 * rest behind a fixed-width run of dots (fixed so the mask does not leak the address's length), and
 * keep the domain, which is the part that actually tells a facilitator something.
 *
 *   taylor.brooks@example.com → t•••s@example.com
 *   jo@example.org           → j•••@example.org
 */
export function maskEmail(input: string): { mask: string; domain: string } | null {
  const s = input.trim();
  if (!isEmailLike(s)) return null;
  const at = s.lastIndexOf("@");
  const local = s.slice(0, at);
  const domain = s.slice(at + 1).toLowerCase();
  const dots = MASK_CHAR.repeat(3);
  const mask = local.length > 3 ? `${local[0]}${dots}${local.at(-1)}` : `${local[0]}${dots}`;
  return { mask: `${mask.toLowerCase()}@${domain}`, domain };
}

/* --------------------------------------- device & browser --------------------------------------- */

/** Coarse device label from a user agent. Returns null when nothing is recognisable. */
export function describeDevice(ua: string | null | undefined): string | null {
  if (!ua) return null;
  const s = ua.toLowerCase();
  if (s.includes("ipad")) return "iPad";
  if (s.includes("iphone")) return "iPhone";
  if (s.includes("ipod")) return "iPod";
  if (s.includes("android")) return s.includes("mobile") ? "Android phone" : "Android tablet";
  if (s.includes("cros")) return "Chromebook";
  if (s.includes("windows")) return "Windows PC";
  if (s.includes("macintosh") || s.includes("mac os x")) return "Mac";
  if (s.includes("linux")) return "Linux";
  if (s.includes("playstation") || s.includes("xbox") || s.includes("nintendo")) return "Console";
  return null;
}

/** Browser family from a user agent, ordered so wrappers win over the engines they embed. */
export function describeBrowser(ua: string | null | undefined): string | null {
  if (!ua) return null;
  const s = ua.toLowerCase();
  if (s.includes("edg/") || s.includes("edga/") || s.includes("edgios/")) return "Edge";
  if (s.includes("opr/") || s.includes("opera")) return "Opera";
  if (s.includes("samsungbrowser")) return "Samsung Internet";
  if (s.includes("firefox") || s.includes("fxios")) return "Firefox";
  if (s.includes("crios") || s.includes("chrome") || s.includes("chromium")) return "Chrome";
  if (s.includes("safari")) return "Safari";
  return null;
}

/* ------------------------------------------ geography ------------------------------------------ */

const REGION_INDICATOR = 0x1f1e6;

/** Flag emoji for an ISO-3166-1 alpha-2 code ("US" → 🇺🇸). Empty string when the code is unusable. */
export function countryFlag(code: string | null | undefined): string {
  if (!code || !/^[A-Za-z]{2}$/.test(code)) return "";
  const up = code.toUpperCase();
  return String.fromCodePoint(
    REGION_INDICATOR + (up.charCodeAt(0) - 65),
    REGION_INDICATOR + (up.charCodeAt(1) - 65),
  );
}

/** Localised country name, falling back to the raw code when Intl has no display name. */
export function countryName(code: string | null | undefined, locale = "en"): string {
  if (!code) return "";
  const up = code.toUpperCase();
  if (up === "T1") return "Tor network";
  if (up === "XX") return "Unknown";
  try {
    return new Intl.DisplayNames([locale], { type: "region" }).of(up) ?? up;
  } catch {
    return up;
  }
}

/** "Austin, Texas, United States" — as much of it as the edge actually knew. */
export function formatLocation(ctx: Partial<ParticipantContext>, locale = "en"): string {
  const parts = [ctx.city, ctx.region, countryName(ctx.country, locale)].filter(
    (p): p is string => Boolean(p) && String(p).trim().length > 0,
  );
  // Drop a region that just repeats the city ("Singapore, Singapore").
  const deduped = parts.filter((p, i) => parts.indexOf(p) === i);
  return deduped.join(", ");
}

/** "America/Sao_Paulo" → "Sao Paulo": the readable half of an IANA zone name. */
export function shortTimezone(timezone: string | null | undefined): string {
  if (!timezone) return "";
  const last = timezone.split("/").pop() ?? timezone;
  return last.replaceAll("_", " ");
}

/** Time-zone offset label ("UTC−05:00") for a zone at a given instant. */
export function timezoneOffsetLabel(timezone: string | null | undefined, at = Date.now()): string {
  if (!timezone) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "longOffset",
    }).formatToParts(new Date(at));
    const name = parts.find((p) => p.type === "timeZoneName")?.value ?? "";
    return name.replace("GMT", "UTC").replace("-", "−") || "UTC";
  } catch {
    return "";
  }
}

/* -------------------------------------- context bookkeeping -------------------------------------- */

/** Later, non-null observations win; anything the new capture did not see keeps its old value. */
export function mergeContext(
  prev: ParticipantContext | null,
  next: Partial<ParticipantContext>,
): ParticipantContext {
  const base = prev ?? EMPTY_CONTEXT;
  const out = { ...base };
  for (const [k, v] of Object.entries(next) as [keyof ParticipantContext, unknown][]) {
    if (v === null || v === undefined || v === "") continue;
    // biome-ignore lint/suspicious/noExplicitAny: key-wise copy of a homogeneous record
    (out as any)[k] = v;
  }
  return out;
}

/** True when the capture carries anything worth writing down. */
export function hasContext(ctx: Partial<ParticipantContext> | null | undefined): boolean {
  if (!ctx) return false;
  return Boolean(
    ctx.city || ctx.region || ctx.country || ctx.timezone || ctx.isp || ctx.device || ctx.emailMask,
  );
}

/** Round-trip a context through a header/JSON hop, dropping anything unknown. */
export function parseContext(input: unknown): ParticipantContext {
  if (!input || typeof input !== "object") return { ...EMPTY_CONTEXT };
  const raw = input as Record<string, unknown>;
  const str = (v: unknown, max = 120): string | null =>
    typeof v === "string" && v.trim() ? v.trim().slice(0, max) : null;
  const emailSource = raw.emailSource === "self" || raw.emailSource === "seat" ? raw.emailSource : null;
  return {
    city: str(raw.city),
    region: str(raw.region),
    regionCode: str(raw.regionCode, 8),
    country: str(raw.country, 2)?.toUpperCase() ?? null,
    continent: str(raw.continent, 2)?.toUpperCase() ?? null,
    timezone: str(raw.timezone, 64),
    isp: str(raw.isp, 80),
    asn: typeof raw.asn === "number" && Number.isFinite(raw.asn) ? Math.trunc(raw.asn) : null,
    device: str(raw.device, 32),
    browser: str(raw.browser, 32),
    emailMask: str(raw.emailMask, 254),
    emailDomain: str(raw.emailDomain, 190)?.toLowerCase() ?? null,
    emailSource,
    capturedAt: typeof raw.capturedAt === "number" && Number.isFinite(raw.capturedAt) ? raw.capturedAt : null,
  };
}

/* ------------------------------------------- audience ------------------------------------------- */

export interface AudienceTally {
  /** Stable key (country code, ISP name, …). */
  key: string;
  /** Display label. */
  label: string;
  count: number;
}

/** Aggregate view of the room, rendered as the "Who was in the room" section of the report. */
export interface AudienceSummary {
  /** Everyone counted, players and observers together. */
  total: number;
  players: number;
  observers: number;
  /** How many participants were simulated by the facilitator (they carry no context at all). */
  simulated: number;
  /** How many participants the edge could place geographically. */
  located: number;
  /** How many supplied (or were reserved with) an email address. */
  withEmail: number;
  countries: AudienceTally[];
  regions: AudienceTally[];
  isps: AudienceTally[];
  timezones: AudienceTally[];
  devices: AudienceTally[];
  emailDomains: AudienceTally[];
}

function tally(
  entries: { key: string | null; label: string | null }[],
  locale = "en",
  limit = 12,
): AudienceTally[] {
  const map = new Map<string, AudienceTally>();
  for (const e of entries) {
    const key = e.key?.trim();
    if (!key) continue;
    const existing = map.get(key);
    if (existing) existing.count++;
    else map.set(key, { key, label: (e.label ?? key).trim() || key, count: 1 });
  }
  return [...map.values()]
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, locale))
    .slice(0, limit);
}

export function summarizeAudience(
  participants: { kind: "player" | "viewer"; context: ParticipantContext; simulated?: boolean }[],
  locale = "en",
): AudienceSummary {
  const ctxs = participants.map((p) => p.context);
  return {
    total: participants.length,
    players: participants.filter((p) => p.kind === "player").length,
    observers: participants.filter((p) => p.kind === "viewer").length,
    simulated: participants.filter((p) => p.simulated).length,
    located: ctxs.filter((c) => c.country || c.city).length,
    withEmail: ctxs.filter((c) => c.emailMask).length,
    countries: tally(
      ctxs.map((c) => ({ key: c.country, label: countryName(c.country, locale) })),
      locale,
    ),
    regions: tally(
      ctxs.map((c) => {
        const label = formatLocation({ city: c.city, region: c.region, country: c.country }, locale);
        return { key: label || null, label: label || null };
      }),
      locale,
    ),
    isps: tally(
      ctxs.map((c) => ({ key: c.isp, label: c.isp })),
      locale,
    ),
    timezones: tally(
      ctxs.map((c) => ({ key: c.timezone, label: c.timezone })),
      locale,
    ),
    devices: tally(
      ctxs.map((c) => ({
        key: c.device ? `${c.device}|${c.browser ?? ""}` : null,
        label: c.device && c.browser ? `${c.device} · ${c.browser}` : c.device,
      })),
      locale,
    ),
    emailDomains: tally(
      ctxs.map((c) => ({ key: c.emailDomain, label: c.emailDomain })),
      locale,
    ),
  };
}
